import React from 'react';
import { useTranslation } from '@frontend/i18n';
import { SwitchChipGroup } from '@frontend/design-system';
import {
  PlanQuickFilterTypes,
  usePaymentPlanQuickFilters,
} from '../../../hooks/payment-plans/use-payment-plan-quick-filters';
import { usePaymentPlanSearch } from '../../../hooks/use-payment-plan-search';
import { quickFilterStyles } from '../../../styles/quick-filter.styles';

export const PaymentPlanQuickFilters = () => {
  const { t } = useTranslation('payments');
  const { isFilterActive, handleQuickFilter, clearFilters } = usePaymentPlanQuickFilters();
  const { summary } = usePaymentPlanSearch();

  const buttonValues: PlanQuickFilterTypes[] = [
    'weekFilter',
    'needsActionFilter',
    'pausedStatusFilter',
    'completedStatusFilter',
  ];

  const selectedValues = React.useMemo(() => {
    const selected = buttonValues.filter((v) => isFilterActive(v));
    if (selected.length === 0) {
      return ['all' as PlanQuickFilterTypes];
    }
    return selected;
  }, [isFilterActive]);

  const buttonsConfig = [
    {
      value: 'all',
      label: t('All'),
      style: quickFilterStyles.quickFilterButton,
    },
    {
      value: 'weekFilter',
      label: t('Due this week'),
      style: quickFilterStyles.quickFilterButton,
    },
    {
      value: 'needsActionFilter',
      label: (
        <div css={[quickFilterStyles.notificationBadgeContainer]}>
          <div>{t('Needs Action')}</div>
          <div css={quickFilterStyles.notificationBadge(isFilterActive('needsActionFilter'))}>
            {summary?.needActionPlansCount ?? 0}
          </div>
        </div>
      ),
      style: quickFilterStyles.quickFilterButton,
    },
    {
      value: 'pausedStatusFilter',
      label: t('Paused'),
      style: quickFilterStyles.quickFilterButton,
    },
    {
      value: 'completedStatusFilter',
      label: t('Completed'),
      style: quickFilterStyles.quickFilterButton,
    },
  ] satisfies {
    value: PlanQuickFilterTypes;
    label: React.ReactNode;
    style: object;
  }[];

  const handleOptionChange = (vs: string[] | ((prevState: string[]) => string[])) => {
    const selectedVals = selectedValues.filter((v) => v !== 'all');
    if (typeof vs === 'function') {
      vs = vs(selectedVals.filter((v) => v !== 'weekFilter'));
    }

    if (
      vs.length === 1 &&
      selectedVals.length == 2 &&
      selectedVals.includes('weekFilter') &&
      !vs.includes('weekFilter')
    ) {
      /**
       * weekFilter can be selected while the other filters can also be selected.
       * When de-selecting weekFilter while another filter is active, this override is
       * necessary.
       */
      vs = ['weekFilter'];
    }

    if (Array.isArray(vs)) {
      if (vs.length) {
        if (vs.includes('all')) {
          clearFilters();
          return;
        }
        vs.forEach((v) => handleQuickFilter(v as PlanQuickFilterTypes));
      } else {
        selectedVals.forEach((v) => handleQuickFilter(v));
      }
    }
  };

  return (
    <div
      css={{
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <SwitchChipGroup
        css={quickFilterStyles.buttonContainer}
        checkboxMode
        value={selectedValues}
        setValue={handleOptionChange}
      >
        {buttonsConfig.map(({ label, value }) => {
          return (
            <SwitchChipGroup.Option
              trackingId={`pay-portal-paymentplans-${value.toLocaleLowerCase()}`}
              key={value}
              value={value}
            >
              {label}
            </SwitchChipGroup.Option>
          );
        })}
      </SwitchChipGroup>
    </div>
  );
};
